import { useCallback, useEffect, useRef } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useRouter } from 'next/router';
import { CreateEmployerRestaurantPayload } from '@restworld/data-services';
import { JPOnboardingType } from '../job-position-onboarding/types';
import { JPOnboardingSteps, jpOnboardingStepsAtom } from '../job-position-onboarding/use-job-positions-onboarding';
import {
  activeRestaurantOnboardingStateAtom,
  RestaurantOnboardingStage,
} from '../restaurants/restaurant-onboarding-modal';

// ==================== TYPES ====================

export type OrgOnboardingSteps = 'restaurant_creation' | 'job_position_creation' | 'user_contact_details';

// The ordered list of steps for the organization onboarding flow
export const ORG_ONBOARDING_STEPS: OrgOnboardingSteps[] = [
  'restaurant_creation',
  'job_position_creation',
  'user_contact_details',
];

// Default first sub-step for each org onboarding step
export const ORG_DEFAULT_SUB_STEPS: Record<OrgOnboardingSteps, string> = {
  restaurant_creation: 'select_restaurant_from_google',
  job_position_creation: 'role',
  user_contact_details: 'restaurant_owner_onboarding_form',
};

// User contact details collected during org onboarding
export interface OrgOnboardingContactDetails {
  name: string;
  surname: string;
  email: string;
  phone_number: string;
  privacy_policy: boolean;
  terms_and_conditions: boolean;
}

// Formatted restaurant data structure used during org onboarding
// This matches the payload structure created by useFormatRestaurantOnboardingData
export interface FormattedOrgRestaurantData {
  employer_user_contacts: {
    name: string;
    surname: string;
    email: string;
    phone_number: string;
  };
  restaurants: CreateEmployerRestaurantPayload;
}

// The full payload for creating all entities at once
export interface OrgOnboardingPayload {
  restaurant: CreateEmployerRestaurantPayload | null;
  job_position: Partial<JPOnboardingType> | null;
  contact_details: OrgOnboardingContactDetails | null;
}

// ==================== ATOMS ====================

// LocalStorage keys for persistence
export const ORG_ONBOARDING_STORAGE_KEYS = {
  step: 'org-onboarding-step',
  restaurant: 'org-onboarding-restaurant',
  jpData: 'org-onboarding-jp-data',
  jpStep: 'org-onboarding-jp-step',
  contact: 'org-onboarding-contact',
  existingUserFlow: 'org-onboarding-existing-user-flow',
  existingUserId: 'org-onboarding-existing-user-id',
  existingUserPhone: 'org-onboarding-existing-user-phone',
  conflictingFields: 'org-onboarding-conflicting-fields',
  aiValidationIds: 'org-onboarding-ai-validation-ids',
} as const;

// Current step in the organization onboarding flow (persisted to localStorage)
export const orgOnboardingStepAtom = atomWithStorage<OrgOnboardingSteps>(
  ORG_ONBOARDING_STORAGE_KEYS.step,
  'restaurant_creation',
  undefined,
  { unstable_getOnInit: true }
);
// Flag to indicate that we are in the organization onboarding flow
// This is a runtime flag (NOT persisted) — set on mount by onboarding.tsx
export const isOrgOnboardingModeAtom = atom<boolean>(false);

// Stores the formatted restaurant data collected during venue creation step (persisted)
// Structure: { employer_user_contacts: {...}, restaurants: {...} }
export const orgOnboardingRestaurantDataAtom = atomWithStorage<FormattedOrgRestaurantData | null>(
  ORG_ONBOARDING_STORAGE_KEYS.restaurant,
  null,
  undefined,
  { unstable_getOnInit: true }
);

// Stores the job position form data collected during JP creation step (persisted)
export const orgOnboardingJPDataAtom = atomWithStorage<Partial<JPOnboardingType> | null>(
  ORG_ONBOARDING_STORAGE_KEYS.jpData,
  null,
  undefined,
  { unstable_getOnInit: true }
);

// Stores the current JP sub-step within the JP creation flow (persisted)
// e.g. 'role', 'salary', 'review' — so we can restore the exact JP step on refresh
export const orgOnboardingJPStepAtom = atomWithStorage<JPOnboardingSteps | null>(
  ORG_ONBOARDING_STORAGE_KEYS.jpStep,
  null,
  undefined,
  { unstable_getOnInit: true }
);

// Stores the user contact details collected during the contact step (persisted)
export const orgOnboardingContactDetailsAtom = atomWithStorage<OrgOnboardingContactDetails | null>(
  ORG_ONBOARDING_STORAGE_KEYS.contact,
  null,
  undefined,
  { unstable_getOnInit: true }
);

// Stores the binary pictures (File objects) uploaded during restaurant creation
// This is a COPY from binaryPicturesAtom captured when user proceeds from restaurant step
// Why separate atom? Because resetState() clears binaryPicturesAtom, but we need to preserve
// the pictures until final submission at user_contact_details step
// Note: This is NOT persisted (Files can't be serialized to localStorage)
// User must re-upload if they refresh during onboarding
export const orgOnboardingBinaryPicturesAtom = atom<{ url: string; file: File }[]>([]);

// Flag: user went through the existing user login flow (email conflict → login → return)
// Persisted so it survives the redirect to /login and back
export const orgOnboardingExistingUserFlowAtom = atomWithStorage<boolean>(
  ORG_ONBOARDING_STORAGE_KEYS.existingUserFlow,
  false,
  undefined,
  { unstable_getOnInit: true }
);

// Stores the existing user's ID from the conflict response (persisted for login redirect)
export const orgOnboardingExistingUserIdAtom = atomWithStorage<string | null>(
  ORG_ONBOARDING_STORAGE_KEYS.existingUserId,
  null,
  undefined,
  { unstable_getOnInit: true }
);

// Stores the masked phone number from the conflict response (e.g. "***1234")
// Used to display which number received the OTP
export const orgOnboardingExistingUserPhoneAtom = atomWithStorage<string | null>(
  ORG_ONBOARDING_STORAGE_KEYS.existingUserPhone,
  null,
  undefined,
  { unstable_getOnInit: true }
);

// Stores the conflicting fields from the 409 response (e.g. ["email"], ["phone_number"], or ["email", "phone_number"])
// Used to customize the conflict message shown to the user
export const orgOnboardingConflictingFieldsAtom = atomWithStorage<string[] | null>(
  ORG_ONBOARDING_STORAGE_KEYS.conflictingFields,
  null,
  undefined,
  { unstable_getOnInit: true }
);

// Stores AI validation record IDs returned from validate_ad calls during org onboarding.
// These are sent with the org create payload so the backend can link them to the created JP.
export const orgOnboardingAiValidationIdsAtom = atomWithStorage<string[]>(
  ORG_ONBOARDING_STORAGE_KEYS.aiValidationIds,
  [],
  undefined,
  { unstable_getOnInit: true }
);

// Flag: user clicked "save and continue later" from JP onboarding header within org onboarding
// Runtime only (NOT persisted) — controls which header text variant to show on the contact step
export const orgOnboardingSaveAndContinueLaterAtom = atom<boolean>(false);

// ==================== HOOK ====================

// ==================== URL HELPERS ====================

/**
 * Update the URL with org onboarding step and optional sub-step.
 * Uses shallow routing so Next.js doesn't re-render the page.
 *
 * @param replace - If true, uses router.replace (no new history entry).
 *                  Defaults to false (uses router.push for browser back support).
 */
const updateOrgOnboardingUrl = (
  router: ReturnType<typeof useRouter>,
  step: OrgOnboardingSteps,
  subStep?: string,
  replace = false
) => {
  const currentQuery = router.query as Record<string, string | undefined>;
  // Skip if URL already matches to avoid duplicate history entries
  if (currentQuery.step === step && currentQuery.sub_step === subStep) return;

  // Merge step/sub_step into existing query params — preserves gmaps_place_id and any other params
  const query: Record<string, string> = { ...currentQuery, step } as Record<string, string>;
  if (subStep) {
    query.sub_step = subStep;
  }
  const nav = replace ? router.replace : router.push;
  nav({ pathname: router.pathname, query }, undefined, { shallow: true });
};

/**
 * Hook for managing Organization Onboarding step state and progress.
 * Provides utility functions for step transitions and progress tracking.
 * Also syncs the current org step to URL query params (?step=X).
 */
export const useOrgOnboardingSteps = () => {
  const router = useRouter();
  const currentStep = useAtomValue(orgOnboardingStepAtom);
  const setCurrentStep = useSetAtom(orgOnboardingStepAtom);

  const currentStepIndex = ORG_ONBOARDING_STEPS.indexOf(currentStep);

  // Navigate to the next step
  const goToNextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < ORG_ONBOARDING_STEPS.length) {
      const nextStep = ORG_ONBOARDING_STEPS[nextIndex];
      setCurrentStep(nextStep);
      updateOrgOnboardingUrl(router, nextStep);
    }
  }, [currentStepIndex, setCurrentStep, router]);

  // Navigate to the previous step
  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      const prevStep = ORG_ONBOARDING_STEPS[prevIndex];
      setCurrentStep(prevStep);
      updateOrgOnboardingUrl(router, prevStep);
    }
  }, [currentStepIndex, setCurrentStep, router]);

  // Navigate to a specific step
  const goToStep = useCallback(
    (step: OrgOnboardingSteps) => {
      setCurrentStep(step);
      updateOrgOnboardingUrl(router, step);
    },
    [setCurrentStep, router]
  );

  // Update the URL sub_step without changing the main step
  const updateSubStep = useCallback(
    (subStep: string) => {
      updateOrgOnboardingUrl(router, currentStep, subStep);
    },
    [router, currentStep]
  );

  // Get progress information
  const getProgressInfo = useCallback(() => {
    return {
      currentStep,
    };
  }, [currentStep]);

  // Determine if header should be shown
  const shouldShowHeader = true;

  // Reset the entire org onboarding state
  const resetOrgOnboarding = useCallback(() => {
    setCurrentStep('restaurant_creation');
  }, [setCurrentStep]);

  return {
    currentStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    updateSubStep,
    getProgressInfo,
    shouldShowHeader,
    resetOrgOnboarding,
    isFirstStep: currentStep === ORG_ONBOARDING_STEPS[0],
    isLastStep: currentStep === ORG_ONBOARDING_STEPS[ORG_ONBOARDING_STEPS.length - 1],
  };
};

/**
 * Hook to sync a sub-step value to the URL whenever it changes.
 * Used by step components (restaurant, contact details) to reflect
 * their internal activeUI state in the URL as ?sub_step=X.
 *
 * Only updates URL when the component's owning step matches the current org step.
 *
 * On initial mount, uses router.replace to amend the URL (no extra history entry)
 * since goToNextStep/goToPreviousStep already pushed the main step.
 * Subsequent changes use router.push so browser back/forward works.
 */
export const useOrgSubStepUrlSync = (subStep: string, ownerStep: OrgOnboardingSteps) => {
  const router = useRouter();
  const currentStep = useAtomValue(orgOnboardingStepAtom);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (currentStep !== ownerStep) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      // On initial mount, only update URL if sub_step is not already set.
      // The parent (useOrgOnboardingUrlSync) may have already resolved the correct sub_step;
      // overriding it here with the atom default would cause a wrong redirect.
      const currentSubStep = router.query.sub_step as string | undefined;
      if (!currentSubStep) {
        updateOrgOnboardingUrl(router, currentStep, subStep, true);
      }
      return;
    }

    updateOrgOnboardingUrl(router, currentStep, subStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStep, currentStep, ownerStep]);
};

/**
 * Resolve the sub-step for a given org onboarding step.
 * Priority: persisted atom value > URL param > default first sub-step.
 */
const resolveSubStep = (
  step: OrgOnboardingSteps,
  atomSubStep: string | null | undefined,
  urlSubStep: string | undefined
): string => {
  // Atom value takes precedence (persisted state)
  if (atomSubStep) return atomSubStep;
  // Fall back to URL param
  if (urlSubStep) return urlSubStep;
  // Default to the first sub-step of this step
  return ORG_DEFAULT_SUB_STEPS[step];
};

/**
 * Minimal URL sync hook for org onboarding.
 *
 * Atom values (persisted in localStorage) are the source of truth.
 * - On first render / refresh: push atoms → URL (overwrite stale URL params).
 *   If no sub-step is found in atoms or URL, reset to the first sub-step.
 * - On subsequent URL changes (browser back/forward): sync URL → atoms.
 *
 * Should be called once in the top-level OrganizationOnboarding component.
 */
export const useOrgOnboardingUrlSync = () => {
  const router = useRouter();
  const currentStep = useAtomValue(orgOnboardingStepAtom);
  const setCurrentStep = useSetAtom(orgOnboardingStepAtom);
  const activeUI = useAtomValue(activeRestaurantOnboardingStateAtom);
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const jpStep = useAtomValue(orgOnboardingJPStepAtom);
  const currentJPStep = useAtomValue(jpOnboardingStepsAtom);
  const setJPStep = useSetAtom(jpOnboardingStepsAtom);
  const restaurantData = useAtomValue(orgOnboardingRestaurantDataAtom);
  const hasInitialized = useRef(false);

  // Effect 1: On mount, replace URL with atom values (atoms take precedence, no history entry)
  // If no sub-step is found in persisted atoms, reset to the first sub-step of the current step.
  useEffect(() => {
    if (!router.isReady || hasInitialized.current) return;
    hasInitialized.current = true;

    const urlSubStep = router.query.sub_step as string | undefined;
    let subStep: string;

    if (currentStep === 'job_position_creation') {
      subStep = resolveSubStep(currentStep, jpStep || currentJPStep, urlSubStep);
    } else if (currentStep === 'restaurant_creation') {
      // For restaurant step: if data already exists in atom, default to form view
      const atomUI = restaurantData ? 'restaurant_onboarding_form' : activeUI || undefined;
      subStep = resolveSubStep(currentStep, atomUI, urlSubStep);
    } else {
      // user_contact_details: activeUI atom is shared and not persisted,
      // so on refresh it resets to 'select_restaurant_from_google' which is wrong.
      // Only trust it if it's a valid contact-details sub-step.
      const validContactSubSteps = ['restaurant_owner_onboarding_form', 'otp_verification', 'contact_already_exists'];
      const atomUI = activeUI && validContactSubSteps.includes(activeUI) ? activeUI : undefined;
      subStep = resolveSubStep(currentStep, atomUI, urlSubStep);
    }

    // Also sync the resolved sub-step back to atoms so UI matches
    if (currentStep === 'job_position_creation') {
      setJPStep(subStep as JPOnboardingSteps);
    } else {
      setActiveUI(subStep as RestaurantOnboardingStage);
    }

    updateOrgOnboardingUrl(router, currentStep, subStep, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  // Effect 2: On URL change (browser back/forward), sync URL → atoms
  useEffect(() => {
    if (!router.isReady || !hasInitialized.current) return;

    const { step, sub_step } = router.query as { step?: string; sub_step?: string };

    // Sync main step from URL
    if (step && ORG_ONBOARDING_STEPS.includes(step as OrgOnboardingSteps)) {
      const orgStep = step as OrgOnboardingSteps;
      setCurrentStep(orgStep);

      // Resolve sub-step: use URL value if present, otherwise reset to default
      const resolvedSubStep = sub_step || ORG_DEFAULT_SUB_STEPS[orgStep];

      if (orgStep === 'job_position_creation') {
        setJPStep(resolvedSubStep as JPOnboardingSteps);
      } else {
        setActiveUI(resolvedSubStep as RestaurantOnboardingStage);
      }

      // If URL is missing sub_step, amend it (replace, no extra history entry)
      if (!sub_step) {
        updateOrgOnboardingUrl(router, orgStep, resolvedSubStep, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, router.isReady, setCurrentStep, setActiveUI, setJPStep]);
};

/**
 * Hook to reset all org onboarding atoms to initial state.
 * Also clears localStorage persistence. Should be called on success or explicit exit.
 */
export const useOrgOnboardingReset = () => {
  const setStep = useSetAtom(orgOnboardingStepAtom);
  const setIsOrgMode = useSetAtom(isOrgOnboardingModeAtom);
  const setRestaurantData = useSetAtom(orgOnboardingRestaurantDataAtom);
  const setJPData = useSetAtom(orgOnboardingJPDataAtom);
  const setJPStep = useSetAtom(orgOnboardingJPStepAtom);
  const setContactDetails = useSetAtom(orgOnboardingContactDetailsAtom);
  const setBinaryPictures = useSetAtom(orgOnboardingBinaryPicturesAtom);
  const setExistingUserFlow = useSetAtom(orgOnboardingExistingUserFlowAtom);
  const setExistingUserId = useSetAtom(orgOnboardingExistingUserIdAtom);
  const setExistingUserPhone = useSetAtom(orgOnboardingExistingUserPhoneAtom);
  const setConflictingFields = useSetAtom(orgOnboardingConflictingFieldsAtom);
  const setSaveAndContinueLater = useSetAtom(orgOnboardingSaveAndContinueLaterAtom);
  const setAiValidationIds = useSetAtom(orgOnboardingAiValidationIdsAtom);

  const resetAll = useCallback(() => {
    setStep('restaurant_creation');
    setIsOrgMode(false);
    setRestaurantData(null);
    setJPData(null);
    setJPStep(null);
    setContactDetails(null);
    setBinaryPictures([]);
    setExistingUserFlow(false);
    setExistingUserId(null);
    setExistingUserPhone(null);
    setConflictingFields(null);
    setSaveAndContinueLater(false);
    setAiValidationIds([]);
  }, [
    setStep,
    setIsOrgMode,
    setRestaurantData,
    setJPData,
    setJPStep,
    setContactDetails,
    setBinaryPictures,
    setExistingUserFlow,
    setExistingUserId,
    setExistingUserPhone,
    setConflictingFields,
    setSaveAndContinueLater,
    setAiValidationIds,
  ]);

  return { resetAll };
};
